/**
 * Viator-inhoud van een activiteit leesbaar maken voor de dealpagina
 * (Viator-certificering, eisen F4 en F4b).
 *
 * WAAR HET VANDAAN KOMT (sinds F4b, 23-09-2026): NIET meer uit de
 * server-gerenderde dealrespons, maar uit de aparte leesroute
 * GET /deals/:id/viator-content van site-backend (#3639), via onze eigen
 * route /api/viator-content (die robots.txt uitsluit). Zo staat er geen
 * Viator-tekst in de HTML die zoekmachines lezen; ons
 * certificeringsformulier zegt "unique Viator content is not indexed".
 *
 * Per activiteit komen er twee blokken:
 *  - `productInfo` (#3568): tickettype en verzamelpunt tonen Viators eigen
 *    (Engelse) tekst RECHTSTREEKS -- dat is feitelijke logistieke
 *    informatie (waar/hoe je het ticket krijgt, waar je moet zijn), geen
 *    mening, en heeft geen eigen woorden nodig; de taalgids wordt vertaald
 *    naar leesbare taalnamen (`languageCodeLabel`), nooit rauw
 *    doorgegeven. `cancellationPolicyText`/`cancellationPolicyType`/
 *    `cancellationFullRefundHours` komen wel mee uit dezelfde leesroute,
 *    maar worden sinds Dennis' besluit van 25-09-2026 ("wij hanteren onze
 *    eigen annuleringsvoorwaarden, ook als Viators voorwaarden
 *    interessanter zijn -- het is een volledig pakket, geen losse
 *    activiteit") NERGENS op de dealpagina getoond: er is GEEN
 *    activiteit-annuleringsblok meer. De klant valt bij annuleren onder
 *    Favotrips eigen pakketvoorwaarden (artikel 7), niet onder een
 *    activiteit-specifieke regel.
 *  - `content` (F4b, per taal sinds 25-09-2026): foto's, omschrijving,
 *    programma, duur, in-/exclusies, aanvullende info. De backend haalt
 *    dit per site-taal in (nl/de/fr naast en -- Viator levert geen Pools,
 *    zie site-backend `VIATOR_CONTENT_LANGUAGES`) via de compliance-audit
 *    met een eigen `Accept-Language`-aanroep per taal; ontbreekt de
 *    gevraagde taal (aanbieder levert 'm niet, of nog niet gecureerd), dan
 *    valt de backend terug op Engels en zet `language: "en"` terwijl
 *    `requestedLanguage` de sitetaal blijft -- zo weet de pagina wanneer
 *    het om een terugval gaat.
 *
 * DEFENSIEF LEZEN: ontbreekt iets of is het rommel, dan valt dat onderdeel
 * weg; niets levert een half blok, een leeg kopje of "onbekend" op.
 *
 * TOONT ALLEEN WAT VIATOR STUURT. Hier wordt niets samengevat, afgeleid,
 * aangevuld of vertaald.
 */

/** Tekst met inhoud, of null. Lege/whitespace-tekst telt als niets. */
function trimmedOrNull(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/** Positief geheel getal, of null. */
function positiveInt(value) {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : null;
}

export const KNOWN_TICKET_TYPE_CODES = ["MOBILE_ONLY"];

/**
 * Tickettype zoals het op de pagina mag. Viator levert meestal een leesbare
 * omschrijving ("Mobile or paper ticket accepted"); soms alleen een code
 * ("MOBILE_ONLY"). Een code tonen we NOOIT rauw: een bekende code krijgt
 * een vertaalde tekst via `ticketTypeCodeKey`, een onbekende code laat het
 * blok weg.
 *
 * Alleen codes die in Viators eigen docs staan ("ticketTypes":
 * ["MOBILE_ONLY"]); een code die we niet kennen raden we niet.
 *
 * @returns {{text: string|null, codeKey: string|null}|null}
 */
export function readTicketType(value) {
  const text = trimmedOrNull(value);
  if (!text) return null;
  if (/^[A-Z0-9_]+$/.test(text)) {
    return KNOWN_TICKET_TYPE_CODES.includes(text)
      ? { text: null, codeKey: text.toLowerCase() }
      : null;
  }
  return { text, codeKey: null };
}

/**
 * Het #3568-viertal uit de leesroute.
 *
 * `cancellationPolicyText`/`cancellationPolicyType`/
 * `cancellationFullRefundHours`: brongegeven over Viators eigen beleid,
 * puur brongegeven -- SINDS 25-09-2026 (Dennis' besluit) door GEEN enkele
 * component meer gebruikt om iets te tonen. De velden blijven hier alleen
 * omdat de leesroute ze meestuurt en de bedradingsproeven
 * (`route.test.js`, `viator-content-not-in-server-html...`) bewijzen dat
 * de camelCase-omzetting van de hele respons klopt; ze bereiken de
 * dealpagina nergens.
 *
 * @returns {{cancellationPolicyText: string|null, cancellationPolicyType: string|null, cancellationFullRefundHours: number|null, languageGuides: {type: string|null, language: string}[], ticketType: {text: string|null, codeKey: string|null}|null, meetingPoints: string[]}|null}
 */
export function readViatorProductInfo(raw) {
  if (!isPlainObject(raw)) return null;

  // De canonieke `supplier`-namen eerst; de oude namen zijn tijdelijke
  // aliassen met dezelfde waarde.
  const cancellationPolicyText =
    trimmedOrNull(raw.supplierCancellationPolicyText) ??
    trimmedOrNull(raw.cancellationPolicyText);
  const cancellationPolicyType = trimmedOrNull(
    raw.supplierCancellationPolicyType,
  );
  const cancellationFullRefundHours = positiveInt(
    raw.supplierCancellationFullRefundHours,
  );
  const ticketType = readTicketType(raw.supplierTicketType ?? raw.ticketType);

  const languageGuides = asArray(raw.languageGuides)
    .map((guide) => ({
      type: trimmedOrNull(guide ? guide.type : null),
      language: trimmedOrNull(guide ? guide.language : null),
    }))
    .filter((guide) => guide.language !== null);

  // Alleen leesbare tekst. Een punt zonder omschrijving valt weg: de
  // backend stuurt nooit meer een aanbiederscode mee, en een code is voor
  // een klant geen verzamelpunt.
  const meetingPoints = asArray(raw.meetingPoints)
    .map((point) => trimmedOrNull(point ? point.description : null))
    .filter((text) => text !== null);

  const hasContent =
    cancellationPolicyText !== null ||
    ticketType !== null ||
    languageGuides.length > 0 ||
    meetingPoints.length > 0;
  if (!hasContent) return null;

  return {
    cancellationPolicyText,
    cancellationPolicyType,
    cancellationFullRefundHours,
    languageGuides,
    ticketType,
    meetingPoints,
  };
}

function readDuration(raw) {
  if (!isPlainObject(raw)) return null;
  if (raw.kind === "fixed") {
    const minutes = positiveInt(raw.minutes);
    return minutes ? { kind: "fixed", minutes } : null;
  }
  if (raw.kind === "variable") {
    const from = positiveInt(raw.fromMinutes);
    const to = positiveInt(raw.toMinutes);
    return from && to && to > from
      ? { kind: "variable", fromMinutes: from, toMinutes: to }
      : null;
  }
  return null;
}

const ADMISSION = new Set(["YES", "NO", "NOT_APPLICABLE"]);

function readStops(raw) {
  return asArray(raw)
    .filter(isPlainObject)
    .map((stop) => ({
      text: trimmedOrNull(stop.text),
      locationName: trimmedOrNull(stop.locationName),
      duration: readDuration(stop.duration),
      admissionIncluded: ADMISSION.has(stop.admissionIncluded)
        ? stop.admissionIncluded
        : null,
      passByWithoutStopping: stop.passByWithoutStopping === true,
    }))
    .filter((stop) => stop.text !== null || stop.locationName !== null);
}

/** Het programma; een onbekende vorm wordt niet getoond (niet geraden). */
function readItinerary(raw) {
  if (!isPlainObject(raw)) return null;
  switch (raw.type) {
    case "STANDARD": {
      const stops = readStops(raw.stops);
      return stops.length > 0 ? { type: "STANDARD", stops } : null;
    }
    case "ACTIVITY": {
      const description = trimmedOrNull(raw.description);
      const dishes = asArray(raw.dishes)
        .filter(isPlainObject)
        .map((dish) => ({
          course: trimmedOrNull(dish.course),
          name: trimmedOrNull(dish.name),
          description: trimmedOrNull(dish.description),
        }))
        .filter((dish) => dish.name !== null);
      if (!description && dishes.length === 0) return null;
      return {
        type: "ACTIVITY",
        description,
        locationName: trimmedOrNull(raw.locationName),
        dishes,
      };
    }
    case "MULTI_DAY_TOUR": {
      const days = asArray(raw.days)
        .filter(isPlainObject)
        .map((day) => ({
          dayNumber: positiveInt(day.dayNumber),
          title: trimmedOrNull(day.title),
          stops: readStops(day.stops),
          accommodations: asArray(day.accommodations)
            .map(trimmedOrNull)
            .filter((a) => a !== null),
        }))
        .filter(
          (day) =>
            day.title !== null ||
            day.stops.length > 0 ||
            day.accommodations.length > 0,
        );
      return days.length > 0 ? { type: "MULTI_DAY_TOUR", days } : null;
    }
    case "HOP_ON_HOP_OFF": {
      const routes = asArray(raw.routes)
        .filter(isPlainObject)
        .map((route) => ({
          name: trimmedOrNull(route.name),
          operatingSchedule: trimmedOrNull(route.operatingSchedule),
          duration: readDuration(route.duration),
          stops: asArray(route.stops)
            .filter(isPlainObject)
            .map((stop) => ({
              text: trimmedOrNull(stop.text),
              locationName: trimmedOrNull(stop.locationName),
            }))
            .filter((stop) => stop.text !== null || stop.locationName !== null),
        }))
        .filter(
          (route) =>
            route.name !== null ||
            route.operatingSchedule !== null ||
            route.stops.length > 0,
        );
      return routes.length > 0 ? { type: "HOP_ON_HOP_OFF", routes } : null;
    }
    case "UNSTRUCTURED": {
      const description = trimmedOrNull(raw.description);
      const itinerary = trimmedOrNull(raw.itinerary);
      if (!description && !itinerary) return null;
      return { type: "UNSTRUCTURED", description, itinerary };
    }
    default:
      return null;
  }
}

/**
 * Alleen https-URL's van Viators foto-CDN. PRECIES de host die de CSP
 * (`img-src` in next.config.mjs) toestaat: een foto van een andere host zou
 * als kapot plaatje op de pagina staan, dus die valt hier al weg.
 */
export const VIATOR_IMAGE_HOST = "hare-media-cdn.tripadvisor.com";

export function isAllowedViatorImageUrl(url) {
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === "https:" &&
      !parsed.username &&
      !parsed.password &&
      parsed.hostname.toLowerCase() === VIATOR_IMAGE_HOST
    );
  } catch {
    return false;
  }
}

function readImages(raw) {
  return asArray(raw)
    .filter(isPlainObject)
    .map((image) => {
      const variants = asArray(image.variants)
        .filter(isPlainObject)
        .map((v) => ({
          url: trimmedOrNull(v.url),
          width: positiveInt(v.width),
          height: positiveInt(v.height),
        }))
        .filter((v) => v.url !== null && isAllowedViatorImageUrl(v.url));
      return { caption: trimmedOrNull(image.caption), variants };
    })
    .filter((image) => image.variants.length > 0);
}

function readListItems(raw) {
  return asArray(raw)
    .map((item) =>
      isPlainObject(item) ? trimmedOrNull(item.text) : trimmedOrNull(item),
    )
    .filter((text) => text !== null);
}

/**
 * Talen die de backend voor productinhoud kan leveren (Viator-inname per
 * taal, 25-09-2026, `pl` verwijderd 25-09-2026 -- Viator levert geen
 * Pools). Moet gelijk blijven aan `VIATOR_CONTENT_LANGUAGES` in
 * site-backend's `viator-product-content.util.ts`: een taal die daar niet
 * bij staat, schrijft de audit nooit weg, dus hier hoeft niets extra's mee.
 * Een taal die de backend toch stuurt maar hier niet bekend is (nooit
 * verwacht) valt hieronder ook weg -- defensief, geen gok.
 */
const KNOWN_CONTENT_LANGUAGES = new Set(["en", "nl", "de", "fr"]);

/**
 * De F4b-productinhoud uit de leesroute.
 *
 * @returns {null|{language: string, requestedLanguage: string|null, machineTranslated: boolean, description: string|null, images: object[], itinerary: object|null, duration: object|null, inclusions: string[], exclusions: string[], additionalInfo: string[]}}
 */
export function readViatorContent(raw) {
  if (!isPlainObject(raw)) return null;
  const language = trimmedOrNull(raw.language);
  if (!language || !KNOWN_CONTENT_LANGUAGES.has(language.toLowerCase())) {
    return null;
  }
  const content = {
    language: language.toLowerCase(),
    requestedLanguage: trimmedOrNull(raw.requestedLanguage),
    machineTranslated: raw.machineTranslated === true,
    description: trimmedOrNull(raw.description),
    images: readImages(raw.images),
    itinerary: readItinerary(raw.itinerary),
    duration: readDuration(raw.duration),
    inclusions: readListItems(raw.inclusions),
    exclusions: readListItems(raw.exclusions),
    additionalInfo: readListItems(raw.additionalInfo),
  };
  const hasContent =
    content.description !== null ||
    content.images.length > 0 ||
    content.itinerary !== null ||
    content.duration !== null ||
    content.inclusions.length > 0 ||
    content.exclusions.length > 0 ||
    content.additionalInfo.length > 0;
  return hasContent ? content : null;
}

/**
 * Het blok van EEN activiteit uit de respons van /api/viator-content.
 *
 * @returns {{productInfo: object|null, content: object|null}|null}
 */
export function readViatorActivityContent(payload, activityId) {
  const id = Number(activityId);
  if (!Number.isInteger(id) || id <= 0) return null;
  const data = isPlainObject(payload)
    ? isPlainObject(payload.data)
      ? payload.data
      : payload
    : null;
  const entry = asArray(data ? data.activities : null).find(
    (a) => isPlainObject(a) && Number(a.activityId) === id,
  );
  if (!entry) return null;
  const productInfo = readViatorProductInfo(entry.productInfo);
  const content = readViatorContent(entry.content);
  return productInfo || content ? { productInfo, content } : null;
}

/**
 * Duur leesbaar in de taal van de bezoeker, zonder eigen tabel:
 * `Intl.NumberFormat` met eenheid (CLDR), dus "2 uur", "2 Stunden",
 * "2 heures", "2 hours", "2 godziny".
 */
export function formatDurationMinutes(minutes, locale = "nl") {
  const total = positiveInt(minutes);
  if (!total) return null;
  const fmt = (value, unit) =>
    new Intl.NumberFormat(locale || "nl", {
      style: "unit",
      unit,
      unitDisplay: "long",
    }).format(value);
  if (total % 1440 === 0) return fmt(total / 1440, "day");
  if (total < 60) return fmt(total, "minute");
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest === 0
    ? fmt(hours, "hour")
    : `${fmt(hours, "hour")} ${fmt(rest, "minute")}`;
}

/**
 * Taalcode leesbaar maken in de taal van de bezoeker: `nl` wordt
 * "Nederlands" op de NL-site en "Niederländisch" op de DE-site.
 *
 * `Intl.DisplayNames`, de ingebouwde CLDR-lijst: geen eigen tabel om bij
 * te houden en meteen in alle vijf de sitetalen goed.
 *
 * Kent de browser de code niet, dan geeft `Intl` de code onvertaald terug;
 * dan tonen we hem in hoofdletters ("ZZ"). Nooit een verzonnen taalnaam.
 */
export function languageCodeLabel(code, locale = "nl") {
  const raw = trimmedOrNull(code);
  if (!raw) return null;
  const normalised = raw.replace(/_/g, "-");
  try {
    const displayNames = new Intl.DisplayNames([locale || "nl"], {
      type: "language",
    });
    const label = displayNames.of(normalised);
    if (label && label.toLowerCase() !== normalised.toLowerCase()) return label;
  } catch {
    // Structureel ongeldige code: Intl gooit een RangeError. Val terug.
  }
  return raw.toUpperCase();
}
